import { FC } from 'react';
import { PractitionerFormValue } from '../../PractitionerSchedule/PractitionerSchedule';
import { useFormContext } from 'react-hook-form';
import { LunchBreakItem } from 'utils/normalizeEventBreak';
import CopyTimeSlot from 'pages/OfficeSchedulePage/DayTimeSlot/DefaultTimeView/CopyTimeSlot/CopyTimeSlot';
import { ReactComponent as CloseIcon } from 'assets/icons/clear.svg';
import SelectTime from './SelectTime/SelectTime';

interface LunchBreakSectionProps {
  day: string;
}

const LunchBreakSection: FC<LunchBreakSectionProps> = ({ day }) => {
  const { watch, setValue } = useFormContext<PractitionerFormValue>();

  const {
    weeklyTimeSlotLunchBreak,
    isAdvancedSchedule,
    advancedWeeklyTimeSlotLunchBreak,
    advancedWeeklyTimeSlot,
    weeklyTimeSlot,
  } = watch();

  const currentLunchBreak = isAdvancedSchedule
    ? advancedWeeklyTimeSlotLunchBreak
    : weeklyTimeSlotLunchBreak;

  const currentSchedule = isAdvancedSchedule
    ? advancedWeeklyTimeSlot
    : weeklyTimeSlot;

  const property = isAdvancedSchedule
    ? 'advancedWeeklyTimeSlotLunchBreak'
    : 'weeklyTimeSlotLunchBreak';

  const currentDayLunchBreak = currentLunchBreak.find(
    (lunchBreakDay) => lunchBreakDay.day === day
  )!;

  const currentDaySchedule = currentSchedule.find(
    (scheduleDay) => scheduleDay.day === day
  )!;

  const isCurrentLunchBreakNotSet =
    currentDayLunchBreak.startFirst === null &&
    currentDayLunchBreak.endFirst === null &&
    currentDayLunchBreak.startSecond === null &&
    currentDayLunchBreak.endSecond === null;

  const isLunchBreakWithFirstSet =
    currentDayLunchBreak.startFirst !== null &&
    currentDayLunchBreak.endFirst !== null &&
    currentDayLunchBreak.startSecond === null &&
    currentDayLunchBreak.endSecond === null;

  const isLunchBreakWithAllSet =
    currentDayLunchBreak.startFirst !== null &&
    currentDayLunchBreak.endFirst !== null &&
    currentDayLunchBreak.startSecond !== null &&
    currentDayLunchBreak.endSecond !== null;

  const onAddLunchBreak = () => {
    const updatedLunchBreaks = [...currentLunchBreak].map((item) => {
      if (item.day !== day) return item;
      const updatedItem: LunchBreakItem = {
        ...item,
        startFirst: currentDaySchedule.start,
        endFirst: currentDaySchedule.end,
      };
      return updatedItem;
    });
    setValue(property, updatedLunchBreaks, { shouldDirty: true });
  };

  const onAddSecondLunchBreak = () => {
    const updatedLunchBreaks = [...currentLunchBreak].map((item) => {
      if (item.day !== day) return item;
      const updatedItem: LunchBreakItem = {
        ...item,
        startSecond: currentDayLunchBreak.endFirst,
        endSecond: currentDaySchedule.end,
      };
      return updatedItem;
    });
    setValue(property, updatedLunchBreaks, { shouldDirty: true });
  };

  const onRemoveFirstLunchBreak = () => {
    const updatedLunchBreaks = [...currentLunchBreak].map((item) => {
      if (item.day !== day) return item;
      const updatedItem: LunchBreakItem = {
        ...item,
        startFirst: null,
        endFirst: null,
      };
      return updatedItem;
    });
    setValue(property, updatedLunchBreaks, { shouldDirty: true });
  };

  const onRemoveSecondLunchBreak = () => {
    const updatedLunchBreaks = [...currentLunchBreak].map((item) => {
      if (item.day !== day) return item;
      const updatedItem: LunchBreakItem = {
        ...item,
        startSecond: null,
        endSecond: null,
      };
      return updatedItem;
    });
    setValue(property, updatedLunchBreaks, { shouldDirty: true });
  };

  const onRemoveFirstLunchBreakForAll = () => {
    const updatedLunchBreaks = [...currentLunchBreak].map((item) => {
      if (item.day !== day) return item;
      const updatedItem: LunchBreakItem = {
        ...item,
        startFirst: item.startSecond,
        endFirst: item.endSecond,
        startSecond: null,
        endSecond: null,
      };
      return updatedItem;
    });
    setValue(property, updatedLunchBreaks, { shouldDirty: true });
  };

  const onChange = (
    day: string,
    value: string,
    type: string,
    position: 'First' | 'Second' = 'First'
  ) => {
    const updatedLunchBreaks = [...currentLunchBreak].map((item) => {
      if (item.day !== day) return item;
      const updatedItem: LunchBreakItem = {
        ...item,
        [`${type}${position}`]: value,
      };
      return updatedItem;
    });
    setValue(property, updatedLunchBreaks, { shouldDirty: true });
  };

  const mergeTimeSlots = currentLunchBreak.map((lunchBreak, index) => ({
    ...lunchBreak,
    ...currentSchedule[index],
  }));

  const onCopy = (timeSlots: any) => {
    const timeSlotsAsType = timeSlots as Array<
      (typeof mergeTimeSlots)[0] & {
        checked: boolean;
      }
    >;

    const updatedLunchBreaks = timeSlotsAsType.map((timeSlot) => {
      const { checked, isAnimate, operatories, ...props } = timeSlot;
      if (!checked) return { ...props };
      return { ...props, ...currentDayLunchBreak, day: timeSlot.day };
    });

    setValue(property, updatedLunchBreaks, { shouldDirty: true });
  };

  return (
    <>
      {isCurrentLunchBreakNotSet && (
        <div className="pl-5.2">
          <button className="text-magenta font-bold" onClick={onAddLunchBreak}>
            + Add lunch break
          </button>
        </div>
      )}
      {isLunchBreakWithFirstSet && (
        <div className="pl-5.2 pr-4.8">
          <div className="flex justify-between items-center ">
            <span className="text-13 leading-[2rem]">Lunch break(s):</span>
            <CopyTimeSlot
              day={day}
              start={currentDayLunchBreak.startFirst!}
              end={currentDayLunchBreak.endFirst!}
              timeSlots={mergeTimeSlots}
              onCopy={onCopy}
              isClosedDateDisabled
            />
          </div>

          <div className="flex items-center gap-x-0.8 pl-8.5 py-0.6">
            <SelectTime
              day={day}
              type={'start'}
              start={currentDaySchedule.start!}
              end={currentDayLunchBreak.endFirst!}
              value={currentDayLunchBreak.startFirst!}
              onChange={(...args) => onChange(...args)}
            />
            <span className="text-13 leading-[2rem]">-</span>
            <SelectTime
              day={day}
              type={'end'}
              start={currentDayLunchBreak.startFirst!}
              end={currentDaySchedule.end!}
              value={currentDayLunchBreak.endFirst!}
              onChange={(...args) => onChange(...args)}
            />
            <button className="ml-2.7" onClick={onRemoveFirstLunchBreak}>
              <CloseIcon className="w-1.6 h-1.6 fill-darkest-grey" />
            </button>
          </div>

          <button
            className="text-magenta font-bold"
            onClick={onAddSecondLunchBreak}
          >
            + Add another
          </button>
        </div>
      )}
      {isLunchBreakWithAllSet && (
        <div className="pl-5.2 pr-4.8">
          <div className="flex justify-between items-center ">
            <span className="text-13 leading-[2rem]">Lunch break(s):</span>
            <CopyTimeSlot
              day={day}
              start={currentDayLunchBreak.startFirst!}
              end={currentDayLunchBreak.endFirst!}
              timeSlots={mergeTimeSlots}
              onCopy={onCopy}
              isClosedDateDisabled
            />
          </div>

          <div className="flex items-center gap-x-0.8 pl-8.5 py-0.6">
            <SelectTime
              day={day}
              type={'start'}
              start={currentDaySchedule.start!}
              end={currentDaySchedule.end!}
              value={currentDayLunchBreak.startFirst!}
              onChange={(...args) => onChange(...args)}
            />
            <span className="text-13 leading-[2rem]">-</span>
            <SelectTime
              day={day}
              type={'end'}
              start={currentDaySchedule.start!}
              end={currentDaySchedule.end!}
              value={currentDayLunchBreak.endFirst!}
              onChange={(...args) => onChange(...args)}
            />
            <button className="ml-2.7" onClick={onRemoveFirstLunchBreakForAll}>
              <CloseIcon className="w-1.6 h-1.6 fill-darkest-grey" />
            </button>
          </div>

          <div className="flex items-center gap-x-0.8 pl-8.5 py-0.6">
            <SelectTime
              day={day}
              type={'start'}
              start={currentDayLunchBreak.endFirst!}
              end={currentDayLunchBreak.endSecond!}
              value={currentDayLunchBreak.startSecond!}
              onChange={(...args) => onChange(...args, 'Second')}
            />
            <span className="text-13 leading-[2rem]">-</span>
            <SelectTime
              day={day}
              type={'end'}
              start={currentDayLunchBreak.startSecond!}
              end={currentDaySchedule.end!}
              value={currentDayLunchBreak.endSecond!}
              onChange={(...args) => onChange(...args, 'Second')}
            />
            <button className="ml-2.7" onClick={onRemoveSecondLunchBreak}>
              <CloseIcon className="w-1.6 h-1.6 fill-darkest-grey" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default LunchBreakSection;
